import React, { useState, useEffect, useCallback } from 'react';
import { jobKitsAPI, partRequestsAPI, warehouseAPI } from '../lib/api';
import { jobTemplatesAPI } from '../lib/v2Api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Loader2, Package, CheckCircle2, Clock, Lock, Unlock,
  Trash2, Edit2, ChevronRight, Briefcase, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { format } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-800',
  'Ready': 'bg-green-100 text-green-800',
  'Closed': 'bg-gray-100 text-gray-800',
};

const STATUS_ICONS = {
  'Open': Clock,
  'Ready': CheckCircle2,
  'Closed': Lock,
};

export const JobKits = () => {
  const { hasPerm } = useAuth();
  const canManage = hasPerm('po.approve');
  
  const [loading, setLoading] = useState(true);
  const [kits, setKits] = useState([]);
  
  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    job_number: '',
    job_name: '',
    notes: '',
    part_request_ids: [],
  });
  const [saving, setSaving] = useState(false);
  
  // Available part requests for adding to kit
  const [availableRequests, setAvailableRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // View/Edit modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);
  const [loadingKit, setLoadingKit] = useState(false);
  
  // Add requests modal
  const [addRequestsModalOpen, setAddRequestsModalOpen] = useState(false);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingKit, setDeletingKit] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Template mode state (Stage 12D)
  const [createMode, setCreateMode] = useState('manual'); // 'manual' | 'template'
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  // Fetch kits
  const fetchKits = async () => {
    setLoading(true);
    try {
      const response = await jobKitsAPI.list();
      setKits(response.data || []);
    } catch (error) {
      console.error('Error fetching kits:', error);
      toast.error('Failed to load job kits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKits();
  }, []);

  // Fetch available part requests (not in any kit, not cancelled)
  const fetchAvailableRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await partRequestsAPI.list();
      // Filter: not cancelled, not already in a kit
      const available = (response.data || []).filter(
        pr => pr.status !== 'Cancelled' && !pr.kit_id
      );
      setAvailableRequests(available);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Open create modal
  const openCreateModal = async () => {
    setFormData({
      name: '',
      job_number: '',
      job_name: '',
      notes: '',
      part_request_ids: [],
    });
    setCreateMode('manual');
    setSelectedTemplateId('');
    setSelectedWarehouseId('');
    setCreateModalOpen(true);
    await fetchAvailableRequests();
    // Fetch templates and warehouses for template mode
    try {
      const [tRes, wRes] = await Promise.all([
        jobTemplatesAPI.list(),
        warehouseAPI.listWarehouses(),
      ]);
      setTemplates(tRes.data || []);
      setWarehouses(wRes.data || []);
    } catch { /* non-critical */ }
  };

  // Create kit
  const handleCreate = async () => {
    if (createMode === 'template') {
      // Template mode — generate kit via template API
      if (!selectedTemplateId) { toast.error('Select a template'); return; }
      if (!formData.job_number.trim()) { toast.error('Job number is required'); return; }
      if (!selectedWarehouseId) { toast.error('Select a warehouse'); return; }
      setSaving(true);
      try {
        const res = await jobTemplatesAPI.generateKit(selectedTemplateId, {
          job_number: formData.job_number.trim(),
          job_name: formData.job_name.trim() || null,
          warehouse_id: selectedWarehouseId,
          kit_name: formData.name.trim() || null,
        });
        toast.success(`Job kit "${res.data.kit_name}" created from template with ${res.data.items_count} items`);
        setCreateModalOpen(false);
        fetchKits();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to generate kit from template');
      } finally {
        setSaving(false);
      }
      return;
    }
    // Manual mode (original)
    if (!formData.name.trim() || !formData.job_number.trim()) {
      toast.error('Kit name and job number are required');
      return;
    }
    
    setSaving(true);
    try {
      await jobKitsAPI.create(formData);
      toast.success('Job kit created');
      setCreateModalOpen(false);
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create kit');
    } finally {
      setSaving(false);
    }
  };

  // View kit details
  const openViewModal = async (kit) => {
    setSelectedKit(null);
    setViewModalOpen(true);
    setLoadingKit(true);
    try {
      const response = await jobKitsAPI.get(kit.id);
      setSelectedKit(response.data);
    } catch (error) {
      toast.error('Failed to load kit details');
      setViewModalOpen(false);
    } finally {
      setLoadingKit(false);
    }
  };

  // Open add requests modal
  const openAddRequestsModal = async () => {
    setSelectedRequestIds([]);
    setAddRequestsModalOpen(true);
    await fetchAvailableRequests();
  };

  // Add requests to kit
  const handleAddRequests = async () => {
    if (selectedRequestIds.length === 0) {
      toast.error('Select at least one request to add');
      return;
    }
    
    setSaving(true);
    try {
      const response = await jobKitsAPI.addRequests(selectedKit.id, selectedRequestIds);
      setSelectedKit(response.data);
      toast.success(`Added ${selectedRequestIds.length} request(s) to kit`);
      setAddRequestsModalOpen(false);
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add requests');
    } finally {
      setSaving(false);
    }
  };

  // Remove request from kit
  const handleRemoveRequest = async (requestId) => {
    try {
      const response = await jobKitsAPI.removeRequests(selectedKit.id, [requestId]);
      setSelectedKit(response.data);
      toast.success('Request removed from kit');
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove request');
    }
  };

  // Close/Reopen kit
  const handleToggleKitStatus = async () => {
    try {
      let response;
      if (selectedKit.status === 'Closed') {
        response = await jobKitsAPI.reopen(selectedKit.id);
        toast.success('Kit reopened');
      } else {
        response = await jobKitsAPI.close(selectedKit.id);
        toast.success('Kit closed');
      }
      setSelectedKit(response.data);
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update kit status');
    }
  };

  // Delete kit
  const openDeleteConfirm = (kit) => {
    setDeletingKit(kit);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingKit) return;
    setDeleting(true);
    try {
      await jobKitsAPI.delete(deletingKit.id);
      toast.success('Kit deleted');
      setDeleteConfirmOpen(false);
      setViewModalOpen(false);
      fetchKits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete kit');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle request selection
  const toggleRequestSelection = (requestId) => {
    setSelectedRequestIds(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Toggle request selection for create form
  const toggleCreateRequestSelection = (requestId) => {
    setFormData(prev => ({
      ...prev,
      part_request_ids: prev.part_request_ids.includes(requestId)
        ? prev.part_request_ids.filter(id => id !== requestId)
        : [...prev.part_request_ids, requestId]
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-kits-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6" />
              Job Kits
            </h1>
            <p className="text-muted-foreground mt-1 hidden sm:block">
              Group part requests into kits for job readiness tracking
            </p>
          </div>
          {canManage && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button onClick={openCreateModal} data-testid="create-kit-btn" className="hidden md:inline-flex">
                <Plus className="w-4 h-4 mr-2" />
                New Kit
              </Button>
            </TooltipTrigger><TooltipContent>Create a kit to group part requests for a job.</TooltipContent></Tooltip></TooltipProvider>
          )}
        </div>

        {/* Kit List */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {kits.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No job kits yet</p>
                <p className="text-sm mt-1">Create a kit to group part requests for a job</p>
              </CardContent>
            </Card>
          ) : (
            kits.map(kit => {
              const StatusIcon = STATUS_ICONS[kit.status] || Clock;
              return (
                <Card 
                  key={kit.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openViewModal(kit)}
                  data-testid={`kit-card-${kit.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">{kit.name}</CardTitle>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Briefcase className="w-3 h-3" />
                          {kit.job_number}
                        </div>
                      </div>
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Badge className={`cursor-help ${STATUS_COLORS[kit.status]}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {kit.status}
                        </Badge>
                      </TooltipTrigger><TooltipContent>
                        {kit.status === 'Open' ? 'Kit is open and accepting part requests.' :
                         kit.status === 'Closed' ? 'Kit is closed — all parts fulfilled.' :
                         `Kit status: ${kit.status}`}
                      </TooltipContent></Tooltip></TooltipProvider>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm">
                      <div className="space-y-1">
                        <div className="text-muted-foreground">
                          {kit.request_count} request{kit.request_count !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs">
                          {kit.staged_count + kit.fulfilled_count} / {kit.request_count} ready
                        </div>
                      </div>
                      {kit.is_ready && (
                        <div className="text-green-600 font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Ready
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                      Created {format(new Date(kit.created_at), 'MMM d, yyyy')}
                      {kit.created_by_name && ` by ${kit.created_by_name}`}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Kit Modal */}
        {/* Create Job Kit Drawer */}
        <AppDrawer
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          title="Create Job Kit"
          description="Group part requests together for a job"
          size="lg"
          isDirty={!!(formData.name || formData.job_number || formData.job_name || formData.notes)}
          data-testid="create-kit-drawer"
          footer={
            <>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="save-kit-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Kit
              </Button>
            </>
          }
        >
            <div className="space-y-4">
              {/* Mode Toggle (Stage 12D) */}
              <div className="flex gap-2 p-1 bg-muted rounded-lg" data-testid="kit-mode-toggle">
                <button
                  type="button"
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${createMode === 'manual' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
                  onClick={() => setCreateMode('manual')}
                  data-testid="kit-mode-manual"
                >
                  Manual
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${createMode === 'template' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
                  onClick={() => setCreateMode('template')}
                  data-testid="kit-mode-template"
                >
                  <FileText className="w-3.5 h-3.5 inline mr-1" />
                  From Template
                </button>
              </div>

              {createMode === 'template' && (
                <>
                  <div>
                    <Label>Template *</Label>
                    <Select value={selectedTemplateId || 'none'} onValueChange={(v) => setSelectedTemplateId(v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="kit-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select a template...</SelectItem>
                        {templates.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name} ({t.item_count} parts)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedTemplateId && templates.find(t => t.id === selectedTemplateId) && (
                    <div className="border rounded-lg p-3 bg-muted/30 space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template Parts</p>
                      {templates.find(t => t.id === selectedTemplateId).items.map(item => (
                        <div key={item.item_id} className="flex items-center justify-between text-sm">
                          <span>{item.item_name}</span>
                          <Badge variant="secondary" className="text-xs">{item.quantity}x</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <Label>Warehouse *</Label>
                    <Select value={selectedWarehouseId || 'none'} onValueChange={(v) => setSelectedWarehouseId(v === 'none' ? '' : v)}>
                      <SelectTrigger data-testid="kit-warehouse-select"><SelectValue placeholder="Select a warehouse" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select warehouse...</SelectItem>
                        {warehouses.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div>
                <Label>{createMode === 'template' ? 'Kit Name (auto-generated if blank)' : 'Kit Name *'}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Smith Residence HVAC Kit"
                  data-testid="kit-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Job Number *</Label>
                  <Input
                    value={formData.job_number}
                    onChange={(e) => setFormData({...formData, job_number: e.target.value})}
                    placeholder="e.g., JOB-12345"
                    data-testid="kit-job-number-input"
                  />
                </div>
                <div>
                  <Label>Job Name</Label>
                  <Input
                    value={formData.job_name}
                    onChange={(e) => setFormData({...formData, job_name: e.target.value})}
                    placeholder="e.g., Smith Residence"
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional notes about this kit..."
                  rows={2}
                />
              </div>
              
              {/* Part Requests Selection (manual mode only) */}
              {createMode === 'manual' && (
              <div>
                <Label className="mb-2 block">Add Part Requests (Optional)</Label>
                {loadingRequests ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : availableRequests.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">
                    No available part requests to add
                  </div>
                ) : (
                  <div className="border rounded max-h-48 overflow-y-auto divide-y">
                    {availableRequests.map(pr => (
                      <div 
                        key={pr.id} 
                        className="p-2 flex items-center gap-3 hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={formData.part_request_ids.includes(pr.id)}
                          onCheckedChange={() => toggleCreateRequestSelection(pr.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {pr.job_number || 'No job'} - {pr.job_name || 'Unnamed'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {pr.line_item_count} item{pr.line_item_count !== 1 ? 's' : ''} • {pr.status}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
        </AppDrawer>

        {/* View Kit Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {loadingKit ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : selectedKit && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <DialogTitle className="text-xl">{selectedKit.name}</DialogTitle>
                      <DialogDescription className="mt-1">
                        <span className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          {selectedKit.job_number}
                          {selectedKit.job_name && ` - ${selectedKit.job_name}`}
                        </span>
                      </DialogDescription>
                    </div>
                    <Badge className={STATUS_COLORS[selectedKit.status]}>
                      {selectedKit.status}
                    </Badge>
                  </div>
                </DialogHeader>
                
                <div className="py-4 space-y-4">
                  {/* Status Summary */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded">
                      <div className="text-2xl font-bold">{selectedKit.request_count}</div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded">
                      <div className="text-2xl font-bold text-purple-700">{selectedKit.staged_count}</div>
                      <div className="text-xs text-muted-foreground">Staged</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <div className="text-2xl font-bold text-green-700">{selectedKit.fulfilled_count}</div>
                      <div className="text-xs text-muted-foreground">Fulfilled</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded">
                      <div className="text-2xl font-bold text-yellow-700">{selectedKit.pending_count}</div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                    </div>
                  </div>
                  
                  {selectedKit.is_ready && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2 text-green-800">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Kit is ready to schedule!</span>
                      {selectedKit.ready_at && (
                        <span className="text-sm ml-auto">
                          Ready since {format(new Date(selectedKit.ready_at), 'MMM d, h:mm a')}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {selectedKit.notes && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Notes:</span>
                      <p className="mt-1 p-2 bg-muted/50 rounded">{selectedKit.notes}</p>
                    </div>
                  )}
                  
                  {/* Part Requests List */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Part Requests in Kit</Label>
                      {canManage && selectedKit.status !== 'Closed' && (
                        <Button size="sm" variant="outline" onClick={openAddRequestsModal}>
                          <Plus className="w-4 h-4 mr-1" />
                          Add Requests
                        </Button>
                      )}
                    </div>
                    {selectedKit.part_requests.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground border rounded">
                        No part requests in this kit yet
                      </div>
                    ) : (
                      <div className="border rounded divide-y max-h-64 overflow-y-auto">
                        {selectedKit.part_requests.map(pr => (
                          <div key={pr.id} className="p-3 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {pr.job_number || 'No job'}
                                {pr.job_name && ` - ${pr.job_name}`}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {pr.line_item_count} item{pr.line_item_count !== 1 ? 's' : ''}
                                {pr.total_estimated_cost && ` • ~$${pr.total_estimated_cost.toFixed(2)}`}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {pr.status}
                              </Badge>
                              {canManage && selectedKit.status !== 'Closed' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-destructive h-8 w-8 p-0"
                                  onClick={() => handleRemoveRequest(pr.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {canManage && (
                    <>
                      <Button 
                        variant="outline" 
                        className="text-destructive"
                        onClick={() => openDeleteConfirm(selectedKit)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete Kit
                      </Button>
                      <Button variant="outline" onClick={handleToggleKitStatus}>
                        {selectedKit.status === 'Closed' ? (
                          <>
                            <Unlock className="w-4 h-4 mr-1" />
                            Reopen Kit
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4 mr-1" />
                            Close Kit
                          </>
                        )}
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setViewModalOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Requests Modal */}
        <Dialog open={addRequestsModalOpen} onOpenChange={setAddRequestsModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Part Requests to Kit</DialogTitle>
              <DialogDescription>
                Select part requests to add to "{selectedKit?.name}"
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {loadingRequests ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : availableRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No available part requests to add
                </div>
              ) : (
                <div className="border rounded max-h-64 overflow-y-auto divide-y">
                  {availableRequests.map(pr => (
                    <div 
                      key={pr.id} 
                      className="p-3 flex items-center gap-3 hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleRequestSelection(pr.id)}
                    >
                      <Checkbox
                        checked={selectedRequestIds.includes(pr.id)}
                        onCheckedChange={() => toggleRequestSelection(pr.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {pr.job_number || 'No job'} - {pr.job_name || 'Unnamed'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {pr.line_item_count} item{pr.line_item_count !== 1 ? 's' : ''} • {pr.status}
                          {pr.total_estimated_cost && ` • ~$${pr.total_estimated_cost.toFixed(2)}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddRequestsModalOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleAddRequests} 
                disabled={saving || selectedRequestIds.length === 0}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add {selectedRequestIds.length} Request{selectedRequestIds.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Job Kit</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deletingKit?.name}"? 
                The part requests in this kit will NOT be deleted - they will just be unlinked from the kit.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete} 
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Mobile FAB */}
      {canManage && (
        <MobileFABContainer data-testid="job-kits-fab-container">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); openCreateModal(); }}>
              New Kit
            </MobileFABMenuItem>
          </MobileFABMenu>
          <button
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
            onClick={() => setFabOpen(!fabOpen)}
            data-testid="job-kits-mobile-fab"
          >
            <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
          </button>
        </MobileFABContainer>
      )}
    </Layout>
  );
};

export default JobKits;
